import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ExpensablesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { mapFiltersToVariables } from 'modules/expenses/components/expenses/DataCollection/_Filters/filterUtils'
import { useExpensesDataCollection } from 'modules/expenses/components/expenses/DataCollection/useExpensesDataCollection'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'

import { useMyExpensesFilters } from './useMyExpensesFilters'

interface Props {
  companyId: number
  employeeId: number
}

export const MyExpensesDataCollection = ({ companyId, employeeId }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { sortings, columns } = useExpensesDataCollection({ isMyExpenses: true, locale })
  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const filterDefinitions = useMyExpensesFilters({ companyId })

  const dataSource = useGraphqlDataCollectionSource({
    document: ExpensablesDataCollectionDocument,
    mapData: (data) => data.expenses.expensablesConnection.nodes,
    mapVariables: ({ navigationFilters, sortings: mapSortings, filters }) => {
      const { status, filter } = mapFiltersToVariables(filters)

      const variables = {
        companyId: companyId.toString(),
        employeeIds: [employeeId],
        sortOrder: mapSortings?.[0],
        includeManualDrafts: true,
        status,
        filter,
        from: navigationFilters?.date?.value?.from,
        to: navigationFilters?.date?.value?.to,
      }
      return variables
    },
    paginationType: 'infinite-scroll',
    mapPagination: (data) => ({
      total: data.expenses.expensablesConnection.totalCount ?? 0,
      perPage: 20,
      cursor: data.expenses.expensablesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.expensablesConnection.pageInfo.hasNextPage ?? false,
    }),
    navigationFilters: {
      date: {
        defaultGranularity: 'range',
        defaultValue: {
          from: dateFilters.startDate,
          to: dateFilters.endDate,
        },
        granularity: ['day', 'week', 'month', 'year', 'range'],
        type: 'date-navigator',
      },
    },
    filters: filterDefinitions,
    presets: [
      {
        label: i18n.t('expenses_v2.expense.status.draft'),
        filter: { status: ['draft'] },
      },
      {
        label: i18n.t('expenses_v2.expense.status.pending'),
        filter: { status: ['pending'] },
      },
      {
        label: i18n.t('expenses_v2.expense.status.approved'),
        filter: { status: ['approved'] },
      },
      {
        label: i18n.t('expenses_v2.expense.status.paid'),
        filter: { status: ['paid'] },
      },
    ],
    sortings,
  })

  return (
    <Box paddingX="s12" style={{ height: '100%', overflow: 'auto' }}>
      <OneDataCollection
        id="my-expenses/data-collection/v1"
        storage={{
          features: ['filters', 'sortings'],
        }}
        fullHeight
        emptyStates={{
          'no-data': {
            title: i18n.t('expenses_v2.list.empty_state.no_expenses'),
            description: '',
          },
        }}
        source={dataSource}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnHiding: true,
              allowColumnReordering: true,
              columns,
            },
          },
        ]}
        onStateChange={(state) => {
          const { from, to } = state.navigationFilters?.date?.value || {}
          if (from && to) {
            if (
              dateFilters.startDate.getTime() !== from.getTime() ||
              dateFilters.endDate.getTime() !== to.getTime()
            ) {
              setPageDateFilters(from, to)
            }
          }
        }}
      />
    </Box>
  )
}
